<template>
  <div class="infusion-rate-check">
    <div class="medication-target-selects">
      <div class="med-input-group el-input el-input-group el-input-group--prepend">
        <div class="el-input-group__prepend">Is</div>
        <medication-fluid-selector
          class="med-fluid-selector"
          :selected="state.check.id"
          :case-conf="emitter.caseConf"
          :medications="medications"
          :disabled="readonly"
          :dosage-form-filter="dosageForm => dosageForm.enabledRoutes.find(r => r.key === 'IV_IO_INFUSION')"
          :is-global="isGlobal"
          @select="(treatmentType, treatmentId) => selectMedOrFluid(treatmentType, treatmentId)"
        />
      </div>
      <help-tooltip v-if="medFluidError" type="error" hide-space class="error-tooltip">
        <span>{{ medFluidError.message }}</span>
      </help-tooltip>
      <help-tooltip v-if="ivDisabledError" type="error">
        <span>{{ ivDisabledError.message }}</span>
      </help-tooltip>
      <md-field class="bag-volume-select-field">
        <infusion-bag-volume-select
          :value="state.check.bagVolumes"
          :disabled="readonly"
          :default-any="true"
          :available="isGlobal ? null : availableBagVolumes"
          @input="bagVolumeKeys => updateBagVolumes(bagVolumeKeys)"
        />
      </md-field>
      <help-tooltip v-if="bagVolumeError" type="error">
        <span>{{ bagVolumeError.message }}</span>
      </help-tooltip>
    </div>

    <dosage-count-check
      :disabled="readonly"
      mode="DOSAGE"
      prefix="Rate between"
      suffix="mL/hr"
      :dosage-available="true"
      :count-available="false"
      :dosage="state.check.rateDosage"
      class="dosage-count-check"
      @updateDosage="updateRateDosage"
    />

    <el-card v-if="state.check.id" class="infusion-location-options-container">
      <el-select
        v-close-on-zoom
        :value="state.check.ivInfusionLocation"
        :disabled="readonly"
        placeholder="Any infusion location"
        class="iv-infusion-location-select"
        @input="value => updateIvInfusionLocation(value)"
      >
        <el-option value="IV_SITE" label="IV Site" />
        <el-option value="MEDICATION" label="Medication" />
        <el-option value="FLUID" label="Fluid" />
      </el-select>

      <iv-location-select
        v-if="state.check.ivInfusionLocation === 'IV_SITE'"
        :iv-location="state.check.ivInfusionLocationIv || {}"
        :case-conf="emitter.caseConf"
        :is-global="isGlobal"
        :readonly="readonly"
        :iv-category-error="ivCategoryError"
        :iv-port-error="ivPortError"
        :iv-site-error="ivSiteError"
        :iv-side-error="ivSideError"
        @update="updateIvInfusionLocationIv"
      />

      <medication-fluid-selector
        v-if="state.check.ivInfusionLocation === 'MEDICATION'"
        :multiple="true"
        :show-fluids="false"
        :selected="state.check.ivInfusionLocationMedications || []"
        :case-conf="emitter.caseConf"
        :medications="medications"
        :disabled="readonly"
        :dosage-form-filter="
          dosageForm =>
            dosageForm.enabledRoutes.find(r => r.key === 'IV_IO_INFUSION')
        "
        :is-global="isGlobal"
        class="medication-fluid-selector"
        @select="
          (treatmentType, treatmentIds) => {
            updateIvInfusionLocationMedications(treatmentIds);
          }
        "
      />
      <help-tooltip v-if="locationMedError" type="error">
        <span>{{ locationMedError.message }}</span>
      </help-tooltip>

      <medication-fluid-selector
        v-if="state.check.ivInfusionLocation === 'FLUID'"
        :multiple="true"
        :show-meds="false"
        :selected="state.check.ivInfusionLocationFluids || []"
        :case-conf="emitter.caseConf"
        :medications="medications"
        :disabled="readonly"
        :is-global="isGlobal"
        @select="
          (treatmentType, treatmentIds) => {
            updateIvInfusionLocationFluids(treatmentIds);
          }
        "
      />
      <help-tooltip v-if="locationFluidError" type="error">
        <span>{{ locationFluidError.message }}</span>
      </help-tooltip>
    </el-card>
  </div>
</template>

<script>
import merge from 'lodash/merge';

import InfusionBagVolumeSelect from '~lib/common/components/medications/InfusionBagVolumeSelect.vue';
import HelpTooltip from '~lib/common/components/HelpTooltip';
import MedicationFluidSelector from './common/MedicationFluidSelector';
import IvLocationSelect from './common/IVLocationSelect.vue';
import DosageCountCheck from './common/DosageCountCheck.vue';
import infusionCheckMixins from './common/InfusionCheckMixins';

import { fluids } from '~lib/common/config/fluids'

export default {
  name: 'infusion-rate-check',
  components: {
    MedicationFluidSelector,
    HelpTooltip,
    InfusionBagVolumeSelect,
    DosageCountCheck,
    IvLocationSelect,
  },
  mixins: [ infusionCheckMixins ],
  props: [
    'medications',
    'readonly',
    'emitter',
    'ikey',
    'getData',
    'putData',
    'control',
  ],
  data() {
    const check = this.getData(this.ikey);
    const defaultCheck = {
      id: null,
      bagVolumes: [],
      rateDosage: {
        min: null,
        max: null,
      },
      ivInfusionLocation: null,
      ivInfusionLocationIv: null,
      ivInfusionLocationMedications: null,
      ivInfusionLocationFluids: null,
    };
    return {
      state: {
        check: merge({}, defaultCheck, check),
        selectedType:
          !check || check.type === 'INFUSION_MEDICATION_RATE'
            ? 'MEDICATION'
            : 'FLUID',
      },
    };
  },
  computed: {
    isGlobal() {
      return this.emitter.isGlobal;
    },
    selectedDosageForm() {
      const { id: medicationId } = this.state.check;
      if (this.isGlobal) {
        return this.medications.find(med => med._key === medicationId)
      }
      if (!this.emitter.caseConf) {
        return null;
      }
      return this.emitter.caseConf.dosageForms.find(
        caseDosageForm => caseDosageForm.medicationId === medicationId,
      );
    },
    selectedFluid() {
      if (this.isGlobal) {
        return fluids.find(f => f.key === fluidKey);
      }
      const { id: fluidKey } = this.state.check;
      if (!this.emitter.caseConf) {
        return null;
      }
      return this.emitter.caseConf.fluids.find(fluid => fluid.key === fluidKey);
    },
    selectedDosageFormInfusionRoute() {
      if (!this.selectedDosageForm) {
        return null;
      }
      return this.selectedDosageForm.enabledRoutes.find(
        route => route.key === 'IV_IO_INFUSION',
      );
    },
    availableBagVolumes() {
      if (this.state.selectedType === 'MEDICATION') {
        return this.selectedDosageFormInfusionRoute
          ? this.selectedDosageFormInfusionRoute.options.infusionBagVolumes
          : [];
      } else {
        return this.selectedFluid ? this.selectedFluid.infusionBagVolumes : [];
      }
    },
  },
  methods: {
    selectMedOrFluid(treatmentType, treatmentId) {
      if (treatmentType === 'MEDICATION') {
        this.selectMedication(treatmentId);
      } else if (treatmentType === 'FLUID') {
        this.selectFluid(treatmentId);
      }
    },
    selectMedication(medicationId) {
      this.state.check.id = medicationId;
      this.state.selectedType = 'MEDICATION';
      this.clearBagVolumes();
      this.update();
    },
    selectFluid(fluidId) {
      this.state.check.id = fluidId;
      this.state.selectedType = 'FLUID';
      this.clearBagVolumes();
      this.update();
    },
    updateBagVolumes(bagVolumeKeys) {
      this.state.check.bagVolumes = bagVolumeKeys;
      this.update();
    },
    clearBagVolumes() {
      this.state.check.bagVolumes = [];
    },
    updateRateDosage(dosage) {
      this.state.check.rateDosage = dosage;
      this.update();
    },
    updateIvInfusionLocation(location) {
      this.state.check.ivInfusionLocation = location;
      this.update();
    },
    updateIvInfusionLocationIv(ivLocation) {
      this.state.check.ivInfusionLocationIv = ivLocation;
      this.update();
    },
    updateIvInfusionLocationMedications(medicationIds) {
      this.state.check.ivInfusionLocationMedications = medicationIds;
      this.update();
    },
    updateIvInfusionLocationFluids(fluidIds) {
      this.state.check.ivInfusionLocationFluids = fluidIds;
      this.update();
    },
    update() {
      const { check, selectedType } = this.state;
      this.putData(this.ikey, {
        ...check,
        type: selectedType === 'MEDICATION' ? 'INFUSION_MEDICATION_RATE' : 'INFUSION_FLUID_RATE',
        isCurrent: true,
      });
      this.emitter.trigger('process', this.control);
    },
  }
}
</script>

<style scoped>
.infusion-rate-check {
  min-width: 800px;
}

.medication-target-selects {
  display: flex;
  flex-direction: row;
  align-items: stretch;
}

.med-input-group {
  flex: 3;
}

.med-fluid-selector {
  width: 100%;
}

.bag-volume-select-field {
  flex: 2;
}

.md-field {
  margin: 0;
}

.md-field >>> .md-select {
  height: 100%;
}

.md-field >>> .md-select input {
  height: 100%;
}

.el-card >>> .el-card__body {
  display: flex;
  align-items: stretch;
}

.help-tooltip {
  margin-left: -6px;
}
</style>